// Экран списка партий: скелетон при загрузке → карточки → тап открывает разбор.
// Оркестрация загрузки — services/games.ts (поверх @ext/services/chess-apis).
import { sideForNick, pgnTag, type CoachGame } from '@ext/core/game-sources';
import { t, currentLang } from '../i18n';
import { state, setGames, selectGame } from '../state';
import { hasEvals } from '../services/games';
import { GamesPager } from '../services/games-page';
import { openingLabel } from '../core/opening-name';
import { trackCtaClick } from '../services/track';
import { showBackButton, hideMainButton, openLink, alertMsg } from '../tg';
import { metaFromCoachGame, saveMyGame } from '../services/my-games';

const EXT_URL = 'https://chromewebstore.google.com/detail/chess-analysis/enhokpgcomnpmbjancincfdppkldemhi';

interface CardVM {
  id: string;
  result: 'win' | 'loss' | 'draw' | 'unknown';
  resultLetter: string;
  opponent: string;
  opponentRating: string;
  colorLabel: string;
  kingSrc: string;
  speed: string;
  opening: string;
  date: string;
  score: string;
  instant: boolean;
}

function shortDate(game: CoachGame): string {
  let d: Date | null = null;
  const sec = game.endTime || 0;
  if (sec > 0) {
    d = new Date(sec * 1000);
  } else if (game.date) {
    // «YYYY.MM.DD» / «YYYY-MM-DD» из PGN — распарсим, чтобы не показывать сырьё
    const m = game.date.match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
    if (m) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  if (d && !Number.isNaN(d.getTime())) {
    try {
      return d.toLocaleDateString(currentLang(), { day: 'numeric', month: 'short' });
    } catch {
      /* fall through */
    }
  }
  return game.date || '';
}

function toVM(game: CoachGame): CardVM {
  const side = sideForNick(game, { nick: state.nick });
  const result = String(game.result || '');
  const isDraw = /1\/2|½/.test(result);
  let outcome: CardVM['result'] = 'unknown';
  if (side) {
    if (isDraw) outcome = 'draw';
    else if ((side === 'w' && result.startsWith('1-0')) || (side === 'b' && result.startsWith('0-1'))) outcome = 'win';
    else if (result.startsWith('1-0') || result.startsWith('0-1')) outcome = 'loss';
  } else if (isDraw) {
    outcome = 'draw';
  }
  const letter = outcome === 'win' ? 'W' : outcome === 'loss' ? 'L' : outcome === 'draw' ? 'D' : '–';
  const oppIsWhite = side === 'b';
  const opponent = (oppIsWhite ? game.white : game.black) || '—';
  const oppRating = pgnTag(game.pgn, oppIsWhite ? 'WhiteElo' : 'BlackElo');
  return {
    id: game.id,
    result: outcome,
    resultLetter: letter,
    opponent,
    opponentRating: oppRating && oppRating !== '?' ? oppRating : '',
    colorLabel: side === 'b' ? t('games.black') : t('games.white'),
    kingSrc: side === 'b' ? '/pieces/bK.png' : '/pieces/wK.png',
    speed: game.speed || '',
    opening: openingLabel(game),
    date: shortDate(game),
    score: result || '',
    instant: hasEvals(game.pgn),
  };
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
}

function metaChips(vm: CardVM): string {
  const chips: string[] = [];
  if (vm.speed) chips.push(`<span class="badge">${esc(vm.speed)}</span>`);
  if (vm.score) chips.push(`<span class="badge">${esc(vm.score)}</span>`);
  if (vm.date) chips.push(`<span class="badge">${esc(vm.date)}</span>`);
  return chips.join('');
}

function cardHtml(vm: CardVM): string {
  const rating = vm.opponentRating ? ` <span class="muted mono">${esc(vm.opponentRating)}</span>` : '';
  const line = [vm.colorLabel, vm.speed, vm.opening].filter(Boolean).map(esc).join(' · ');
  const badge = vm.instant
    ? `<span class="badge good">${t('games.badge_instant')}</span>`
    : `<span class="badge">${t('games.badge_engine')}</span>`;
  return `
    <button type="button" class="card game-card" data-id="${esc(vm.id)}">
      <div class="row gap12">
        <span class="result ${vm.result}">${vm.resultLetter}</span>
        <div class="grow" style="text-align:left">
          <strong>${esc(vm.opponent)}${rating}</strong><br>
          <span class="tiny"><img class="piece-dot" src="${vm.kingSrc}" alt=""> ${line}</span>
        </div>
        ${badge}<span class="save-game" data-save-my aria-label="${t('my.add')}">☆</span>
      </div>
      <div class="game-meta">${metaChips(vm)}</div>
    </button>`;
}

function footerCta(): string {
  return `
    <a class="card footer-cta row gap12" data-ext>
      <img class="mini-logo" src="/icon128.png" alt="Chess Analysis">
      <span class="grow"><strong>${t('games.cta_ext_title')}</strong><br><span class="tiny">${t('games.cta_ext_sub')}</span></span>
      <span class="muted">›</span>
    </a>`;
}

function shell(inner: string): string {
  return `
    <header class="topbar">
      <span class="back" data-back>${t('top.back')}</span>
      <span class="top-title"><span class="top-logo" aria-hidden="true"></span>${t('games.title')}</span>
      <span class="top-action">${esc(state.nick)}</span>
    </header>
    <section style="margin:8px 2px 16px">
      <div class="eyebrow" data-count></div>
      <h1 style="margin-top:4px">${t('games.header')}</h1>
      <p class="muted">${t('games.subtitle')}</p>
    </section>
    <div data-body>${inner}</div>`;
}

function skeleton(): string {
  const one = `<article class="card"><div class="row gap12"><span class="skel-pill"></span><div class="grow stack"><span class="skel-line"></span><span class="skel-line" style="width:72%"></span></div></div></article>`;
  return `<section class="skeleton" aria-label="${t('games.loading')}">${one}${one}${one}</section>`;
}

function errorCard(msgKey: string, onRetry: () => void, body: HTMLElement): void {
  body.innerHTML = `
    <section class="card state-card">
      <span class="icon">😕</span>
      <p class="muted">${t(msgKey)}</p>
      <button type="button" class="retry-btn" data-retry>${t('games.retry')}</button>
    </section>${footerCta()}`;
  body.querySelector<HTMLButtonElement>('[data-retry]')?.addEventListener('click', onRetry);
  wireExt(body);
}

function wireExt(scope: HTMLElement): void {
  scope.querySelector<HTMLElement>('[data-ext]')?.addEventListener('click', () => {
    trackCtaClick('games_footer');
    openLink(EXT_URL);
  });
}

function trainLink(): string {
  return `
    <button type="button" class="card train-cta row gap12" data-train>
      <span class="train-icon">🎯</span>
      <span class="grow" style="text-align:left"><strong>${t('games.train_title')}</strong><br><span class="tiny">${t('games.train_sub')}</span></span>
      <span class="muted">›</span>
    </button>`;
}

/** Навесить открытие разбора на карточку. */
function wireCard(el: HTMLElement): void {
  el.querySelector<HTMLElement>('[data-save-my]')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const game = state.games.find((g) => g.id === el.dataset.id);
    if (!game) return;
    void saveMyGame(metaFromCoachGame(game), game.pgn).then(() => {
      (event.currentTarget as HTMLElement).textContent = '★';
    }).catch((e) => alertMsg(t(e instanceof Error && e.message === 'limit' ? 'my.limit' : 'my.save_error')));
  });
  el.addEventListener('click', () => {
    const id = el.dataset.id!;
    selectGame(id);
    location.hash = `#/game/${encodeURIComponent(id)}`;
  });
}

function moreButton(): string {
  return `<button type="button" class="card footer-cta more-btn" data-more>${t('games.show_more')}</button>`;
}

export function renderGames(root: HTMLElement): void {
  if (!state.nick) {
    location.hash = '#/';
    return;
  }
  root.className = 'app v2';
  root.innerHTML = shell(skeleton());
  hideMainButton();
  showBackButton(() => {
    location.hash = '#/';
  });

  const body = root.querySelector<HTMLElement>('[data-body]')!;
  const count = root.querySelector<HTMLElement>('[data-count]')!;
  const all: CoachGame[] = [];
  let pager = new GamesPager(state.source, state.nick);

  function updateCount(): void {
    count.textContent = t('games.count', { n: all.length });
  }

  /** Обновить кнопку «Показать ещё»: лоадер / конец данных = кнопки нет. */
  function refreshMore(loading: boolean): void {
    const slot = body.querySelector<HTMLElement>('[data-more-slot]');
    if (!slot) return;
    if (pager.done) {
      slot.innerHTML = '';
      return;
    }
    slot.innerHTML = loading ? `<p class="muted tiny" style="text-align:center">${t('games.loading_more')}</p>` : moreButton();
    slot.querySelector<HTMLElement>('[data-more]')?.addEventListener('click', () => void loadMore());
  }

  async function loadMore(): Promise<void> {
    refreshMore(true);
    try {
      const page = await pager.next();
      if (location.hash !== '#/games') return;
      const listEl = body.querySelector<HTMLElement>('[data-list]');
      for (const g of page) {
        all.push(g);
        if (listEl) {
          listEl.insertAdjacentHTML('beforeend', cardHtml(toVM(g)));
          wireCard(listEl.lastElementChild as HTMLElement);
        }
      }
      setGames(all);
      updateCount();
      refreshMore(false);
    } catch {
      if (location.hash !== '#/games') return;
      refreshMore(false); // сеть моргнула — оставляем кнопку, юзер повторит
    }
  }

  function renderFirst(page: CoachGame[]): void {
    all.push(...page);
    setGames(all);
    updateCount();
    const cards = page.map((g) => cardHtml(toVM(g))).join('');
    body.innerHTML = `${trainLink()}<section class="game-list" data-list>${cards}</section><div data-more-slot></div>${footerCta()}`;
    body.querySelector<HTMLElement>('[data-train]')?.addEventListener('click', () => {
      location.hash = '#/train';
    });
    body.querySelectorAll<HTMLElement>('[data-id]').forEach(wireCard);
    wireExt(body);
    refreshMore(false);
  }

  const load = (): void => {
    body.innerHTML = skeleton();
    all.length = 0;
    pager = new GamesPager(state.source, state.nick);
    pager
      .next()
      .then((page) => {
        if (location.hash !== '#/games') return;
        if (!page.length) {
          errorCard('games.err_empty', load, body);
          return;
        }
        renderFirst(page);
      })
      .catch((err: unknown) => {
        if (location.hash !== '#/games') return;
        const key = err instanceof Error && err.message === 'nick_not_found' ? 'games.err_not_found' : 'games.err_network';
        errorCard(key, load, body);
      });
  };

  load();
}
